import {
  Modal,
  Space,
  Typography,
  Divider,
  Tag,
  Button,
  Popconfirm,
  message,
  Row,
  Col,
  Flex,
} from "antd";
import moment from "moment";
import {
  capitalizeTranslated,
  formatCompactAmount,
  FormatDateTimeFun,
  formatLabel,
  sourceLookup,
  toCapitalize,
} from "../../../shared";
import {
  ConsentForm,
  MedicalHistory,
  PostOperationInstructions,
  PreOperationInstructions,
} from "../../Printer";
import { useState } from "react";
import { DropDownButton } from "../../utils";
import {
  AuditOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  MedicineBoxOutlined,
  MoreOutlined,
  SolutionOutlined,
} from "@ant-design/icons";
import { ActionButton } from "../../actionButton";
import { GetPermissions } from "../../../shared/permissions";
import { ViewFUEAppointment } from "../../ViewDetail";
import { ClientPrescriptionModal } from "../../../pages/ppointments/ClientPrescriptionModal";
import { services as api } from "../../../services";
import { sessionapi } from "../../../services/sessionapi";
import { useNavigate } from "react-router";
import { can } from "../../../Auth";

const { Title, Text } = Typography;
const ViewSessionModal = ({ open, selectedSession, onClose, call }) => {
  const [visiblePreOpDetails, setVisiblePreOpDetails] = useState(false);
  const [visibleConsentFormModal, setVisibleConsentFormModal] = useState(false);
  const [visible, setVisible] = useState(false);
  const [clientInfo, setClientInfo] = useState(null);
  const [visibleViewModal, setVisibleViewModal] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [clientIdForMedicalHistory, setClientIdForMedicalHistory] =
    useState(null);
  const [prescriptionVisible, setPrescriptionVisible] = useState(false);
  const [collectIds, setCollectIds] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const clientDetail =
    selectedSession?.alldata?.appointment?.client_info || null;

  const handleStatusChange = async (sessionId, newStatus) => {
    try {
      const result = await api?.sessionapi?.updateSessionStatus({
        status: newStatus,
        id: Number(sessionId),
      });
      if (result?.success) {
        message.success(`Session Status updated successfull!`);
        call();
        onClose();
      } else {
        message.error(result?.message || `Failed session status`);
      }
    } catch (error) {
      console.error(error);
      message.error(
        "An unexpected error occurred while updating session status.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setDeleting(true);
    const result = await sessionapi?.deleteSession(id);

    if (result?.success) {
      message.success("Session deleted successfully");
      call();
      onClose();
    } else {
      message.error(result?.message || "Failed to delete");
    }
    setDeleting(false);
  };
  return (
    <div>
      <Modal
        title={
          <Space size={5}>
            <Title level={4} className="my-0">
              Session Details:
            </Title>
            <Tag
              color={
                selectedSession?.status === "pending"
                  ? "#FFA500"
                  : selectedSession?.status === "completed"
                    ? "#52c41a"
                    : "#f5222d"
              }
              className="radius-12"
            >
              {capitalizeTranslated(selectedSession?.status)}
            </Tag>
          </Space>
        }
        zIndex={1000}
        centered
        width={700}
        open={open}
        onOk={onClose}
        onCancel={onClose}
        footer={[
          <Space className="w-100 my-3" style={{ justifyContent: "center" }}>
            {can("Session Calendar Views", "update") && (
              <ActionButton
                title="Update Session"
                type="primary"
                icon={<EditOutlined />}
                onClick={() => {
                  navigate(
                    `/add-appointment?clientId=${selectedSession?.alldata?.appointment?.clientID}`,
                  );
                }}
              />
            )}
            {can("Session Calendar Views", "delete") && (
              <Popconfirm
                title="Are you sure you want to delete this session?"
                onConfirm={() => {
                  handleDelete(selectedSession?.id);
                }}
              >
                <ActionButton danger title="Delete" icon={<DeleteOutlined />} />
              </Popconfirm>
            )}
            {can("Session Calendar Views", "consent form") && (
              <ActionButton
                title="Consent Form"
                icon={<AuditOutlined className="text-orangered" />}
                onClick={() => {
                  setClientInfo(selectedSession?.alldata);
                  setVisible(1);
                }}
              />
            )}
            {can("Session Calendar Views", "pre instructions") && (
              <>
                {selectedSession?.status === "pending" && (
                  <ActionButton
                    title="Pre-Operation"
                    icon={<MedicineBoxOutlined className="text-grey" />}
                    onClick={() => {
                      setVisible(5);
                      setClientInfo(selectedSession?.alldata);
                    }}
                  />
                )}
              </>
            )}
            {can("Session Calendar Views", "post insturction") && (
              <>
                {selectedSession?.status === "completed" && (
                  <ActionButton
                    title="Post Operation"
                    icon={<MedicineBoxOutlined className="text-grey" />}
                    onClick={() => {
                      setVisible(4);
                      setClientInfo(selectedSession?.alldata);
                    }}
                  />
                )}
              </>
            )}
            {can("Session Calendar Views", "medical history") && (
              <ActionButton
                title="Medical History"
                icon={<SolutionOutlined className="text-blue" />}
                onClick={() => {
                  setClientIdForMedicalHistory(
                    selectedSession?.alldata?.appointment?.clientID,
                  );
                  setVisible(2);
                }}
              />
            )}
            {can("Session Calendar Views", "template") && (
              <DropDownButton
                disabled={selectedSession?.alldata?.templates?.length === 0}
                icon={<MedicineBoxOutlined className="text-purple" />}
                menu={{
                  items:
                    selectedSession?.alldata?.templates?.map((template) => ({
                      key: template.id,
                      label: template.name,
                      onClick: () => {
                        setPrescriptionVisible(true);
                        setCollectIds({
                          sessionId: selectedSession?.id,
                          tempId: template?.id,
                        });
                      },
                    })) || [],
                }}
              />
            )}
            {can("Session Calendar Views", "status update") && (
              <DropDownButton
                icon={<MoreOutlined />}
                disabled={selectedSession?.status === "pending" ? false : true}
                menu={{
                  items: (() => {
                    const status = selectedSession?.status;
                    const items = [];
                    if (status !== "completed") {
                      items.push({
                        label: "Completed Session",
                        onClick: () =>
                          handleStatusChange(selectedSession.id, "completed"),
                      });
                    }
                    if (status !== "cancel") {
                      items.push({
                        label: "Cancel Session",
                        onClick: () =>
                          handleStatusChange(selectedSession.id, "cancel"),
                      });
                    }
                    return items;
                  })(),
                }}
              />
            )}
          </Space>,
        ]}
      >
        <div className="my-3 h-500 scroll-bar">
          <Space style={{ justifyContent: "space-between", width: "100%" }}>
            <Text strong>MRN:</Text>
            <Text>{clientDetail?.mrn}</Text>
          </Space>
          <Divider />
          <Space style={{ justifyContent: "space-between", width: "100%" }}>
            <Text strong>Client Name:</Text>
            <Text>{toCapitalize(selectedSession?.patientName)}</Text>
          </Space>
          <Divider />
          <Space style={{ justifyContent: "space-between", width: "100%" }}>
            <Text strong>Father Name:</Text>
            <Text>{toCapitalize(selectedSession?.patientFather)}</Text>
          </Space>
          <Divider />
          <Space style={{ justifyContent: "space-between", width: "100%" }}>
            <Text strong>Age:</Text>
            <Text>{selectedSession?.patientAge} Years</Text>
          </Space>
          <Divider />
          <Space style={{ justifyContent: "space-between", width: "100%" }}>
            <Text strong>Cnic No:</Text>
            <Text>{selectedSession?.cnic}</Text>
          </Space>
          <Divider />
          <Space style={{ justifyContent: "space-between", width: "100%" }}>
            <Text strong>Session Time:</Text>
            <Text>{FormatDateTimeFun(selectedSession?.start)}</Text>
          </Space>
          <Divider />
          <Row className="w-100" justify={"space-between"} gutter={[15, 15]}>
            <Col span={12}>
              <Text strong>Issues:</Text>
            </Col>
            <Col span={12}>
              <Flex wrap justify="end" gap={5}>
                {clientDetail?.medical_info &&
                  Object.entries(clientDetail.medical_info)
                    .filter(
                      ([key, value]) => key.startsWith("is") && value === "1",
                    )
                    .map(([key], i) => (
                      <Tag key={i} className="m-0">
                        {formatLabel(key)}
                      </Tag>
                    ))}
              </Flex>
            </Col>
          </Row>
          <Divider />
          <Space style={{ justifyContent: "space-between", width: "100%" }}>
            <Text strong>Service Type:</Text>
            <Text>{selectedSession?.alldata?.service?.name}</Text>
          </Space>
          <Divider />
          <Space style={{ justifyContent: "space-between", width: "100%" }}>
            <Text strong>Telephone:</Text>
            <Text>
              {selectedSession?.countryCode} {selectedSession?.contact}
            </Text>
          </Space>
          <Divider />
          <Space style={{ justifyContent: "space-between", width: "100%" }}>
            <Text strong>Registration:</Text>
            <Text>
              {moment(clientDetail?.register_date).format("MMM Do YYYY")}
            </Text>
          </Space>
          <Divider />

          <Space style={{ justifyContent: "space-between", width: "100%" }}>
            <Text strong>Occupation:</Text>
            <Text>{selectedSession?.occuption}</Text>
          </Space>
          <Divider />
          <Space style={{ justifyContent: "space-between", width: "100%" }}>
            <Text strong>Source of Information:</Text>
            <Text>{selectedSession?.s_o_info}</Text>
          </Space>
          <Divider />
          {can("Session Calendar Views", "view payment details") && (
            <Space style={{ justifyContent: "space-between", width: "100%" }}>
              <Space>
                <Text strong>Total Amount:</Text>
                <Text>
                  {formatCompactAmount(selectedSession?.payment?.totalamount)}{" "}
                  (PKR)
                </Text>
              </Space>
              <Space>
                <Text strong>Advance:</Text>
                <Text>
                  {formatCompactAmount(selectedSession?.payment?.advanceamount)}{" "}
                  (PKR)
                </Text>
              </Space>
              <Space>
                <Text strong>Remaining:</Text>
                <Text>
                  {formatCompactAmount(selectedSession?.payment?.balanceamount)}{" "}
                  (PKR)
                </Text>
              </Space>
            </Space>
          )}
        </div>
      </Modal>

      <ConsentForm
        visible={visible === 1}
        clientInfo={clientInfo}
        onClose={() => {
          setClientInfo(null);
          setVisible(false);
        }}
      />
      {/* MedicalHistory now receives clientId instead of appointmentID */}
      <MedicalHistory
        visible={visible === 2}
        clientId={clientIdForMedicalHistory}
        onClose={() => {
          setVisible(false);
          setClientIdForMedicalHistory(null);
        }}
      />
      <PostOperationInstructions
        visible={visible === 4}
        clientInfo={clientInfo}
        onClose={() => {
          setVisible(false);
          setClientInfo(null);
        }}
      />
      <PreOperationInstructions
        visible={visible === 5}
        clientInfo={clientInfo}
        onClose={() => {
          setVisible(false);
          setClientInfo(null);
        }}
      />
      <ClientPrescriptionModal
        visible={prescriptionVisible}
        collectIds={collectIds}
        onClose={() => {
          setPrescriptionVisible(false);
          setCollectIds(null);
        }}
      />
    </div>
  );
};
export { ViewSessionModal };
